import {
  CanvasTexture,
  LinearFilter,
  SRGBColorSpace,
  type CanvasTexture as ThreeCanvasTexture,
} from "three";

import type { MatchHudModel } from "../alpha/hud/match-hud-model";

export type HudTextureKind = "top-console" | "status" | "menu" | "ai-button" | "phase-action";

interface HudTextureRequest {
  readonly kind: HudTextureKind;
  readonly width: number;
  readonly height: number;
  readonly model: MatchHudModel;
  readonly label?: string | undefined;
  readonly enabled?: boolean | undefined;
}

const MAX_TEXTURES = 48;

function roundedRectangle(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
}

function fitText(context: CanvasRenderingContext2D, value: string, maximumWidth: number): string {
  if (context.measureText(value).width <= maximumWidth) return value;
  let text = value;
  while (text.length > 1 && context.measureText(`${text}…`).width > maximumWidth) {
    text = text.slice(0, -1);
  }
  return `${text}…`;
}

function drawFrame(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  accent = "#d8bd71",
): void {
  const gradient = context.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, "#142833");
  gradient.addColorStop(0.55, "#07151d");
  gradient.addColorStop(1, "#030b11");
  context.fillStyle = gradient;
  roundedRectangle(context, 2, 2, width - 4, height - 4, Math.min(11, height * 0.15));
  context.fill();
  context.strokeStyle = accent;
  context.lineWidth = 2;
  context.globalAlpha = 0.72;
  context.stroke();
  context.globalAlpha = 1;
  context.strokeStyle = "#705c30";
  context.lineWidth = 1;
  roundedRectangle(context, 7, 7, width - 14, height - 14, Math.min(8, height * 0.12));
  context.stroke();
}

function drawTopConsole(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  model: MatchHudModel,
): void {
  drawFrame(context, width, height);
  const compact = height < 80;
  const left = compact ? 14 : 16;
  const reservedRight = compact ? 160 : 64;
  context.textBaseline = "middle";
  context.fillStyle = "#e7cf8d";
  context.font = `700 ${String(compact ? 12 : 13)}px ui-sans-serif, system-ui, sans-serif`;
  context.fillText(
    `OPPONENT  ◆ ${String(model.opponentBanners)}   HAND ${String(model.opponentHandCount)}   DECK ${String(model.opponentDeckCount)}`,
    left,
    compact ? 18 : 22,
  );
  if (model.aiState !== "hidden" && !compact) {
    context.fillStyle = model.aiState === "paused" ? "#e49a77" : "#8fc9b5";
    context.font = "700 10px ui-sans-serif, system-ui, sans-serif";
    context.fillText(`AI ${model.aiState.toUpperCase()}`, width - reservedRight - 18, 22);
  }
  context.strokeStyle = "#7b6a3d";
  context.globalAlpha = 0.7;
  context.beginPath();
  context.moveTo(left, height * 0.45);
  context.lineTo(width - 62, height * 0.45);
  context.stroke();
  context.globalAlpha = 1;

  const phase = model.phase.replace("-", " ").toUpperCase();
  context.fillStyle = "#f4df9b";
  context.font = `800 ${String(compact ? 12 : 14)}px ui-sans-serif, system-ui, sans-serif`;
  context.fillText(`YOU · TURN ${String(model.turnNumber)} · ${phase}`, left, height * 0.61);
  context.fillStyle = "#bed7d0";
  context.font = `700 ${String(compact ? 10 : 12)}px ui-monospace, SFMono-Regular, monospace`;
  const resources = `F ${String(model.ownResources.food)}   P ${String(model.ownResources.production)}   T ${String(model.ownResources.trade)}   S ${String(model.ownResources.science)}   C ${String(model.ownResources.culture)}   ◆ ${String(model.ownBanners)}`;
  context.fillText(resources, left, height * 0.82);
}

function drawLabelPanel(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  label: string,
  enabled: boolean,
  kind: HudTextureKind,
): void {
  drawFrame(context, width, height, enabled ? "#f0cf72" : "#73694e");
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillStyle = enabled ? "#f7e3aa" : "#958f7d";
  context.font =
    kind === "phase-action"
      ? `800 ${String(Math.max(13, Math.min(17, height * 0.3)))}px ui-sans-serif, system-ui, sans-serif`
      : `800 ${String(Math.max(13, height * 0.34))}px ui-sans-serif, system-ui, sans-serif`;
  context.fillText(fitText(context, label, width - 16), width / 2, height / 2);
}

function createTexture(request: HudTextureRequest): ThreeCanvasTexture {
  const scale = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
  const width = Math.max(2, Math.round(request.width * scale));
  const height = Math.max(2, Math.round(request.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (context === null) throw new Error("Canvas 2D is required for the match HUD.");
  context.scale(scale, scale);
  const cssWidth = width / scale;
  const cssHeight = height / scale;
  context.clearRect(0, 0, cssWidth, cssHeight);
  if (request.kind === "top-console") {
    drawTopConsole(context, cssWidth, cssHeight, request.model);
  } else if (request.kind === "status") {
    drawFrame(context, cssWidth, cssHeight, "#9c8958");
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillStyle = "#eee4c8";
    context.font = `600 ${String(Math.max(10, Math.min(13, cssHeight * 0.35)))}px ui-sans-serif, system-ui, sans-serif`;
    context.fillText(
      fitText(context, request.model.statusMessage, cssWidth - 24),
      cssWidth / 2,
      cssHeight / 2,
    );
  } else {
    drawLabelPanel(
      context,
      cssWidth,
      cssHeight,
      request.label ?? "",
      request.enabled ?? true,
      request.kind,
    );
  }
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

class HudTextTextureCache {
  private readonly textures = new Map<string, ThreeCanvasTexture>();

  get(request: HudTextureRequest): ThreeCanvasTexture {
    const key = JSON.stringify(request);
    const cached = this.textures.get(key);
    if (cached !== undefined) {
      this.textures.delete(key);
      this.textures.set(key, cached);
      return cached;
    }
    const texture = createTexture(request);
    this.textures.set(key, texture);
    while (this.textures.size > MAX_TEXTURES) {
      const oldestKey = this.textures.keys().next().value as string | undefined;
      if (oldestKey === undefined) break;
      this.textures.get(oldestKey)?.dispose();
      this.textures.delete(oldestKey);
    }
    return texture;
  }

  size(): number {
    return this.textures.size;
  }
}

export const hudTextTextureCache = new HudTextTextureCache();
